import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": "https://www.reignitespirit.com/about#aboutpage",
  url: "https://www.reignitespirit.com/about",
  name: "About Reignite Spirit",
  description:
    "Learn why Reignite Spirit was created and how the platform supports more informed career transitions.",
  isPartOf: {
    "@id": "https://www.reignitespirit.com/#website",
  },
  about: {
    "@id": "https://www.reignitespirit.com/#organization",
  },
};

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-[#0F0A24] text-white px-6 py-16">
      <SEO
        title="About Reignite Spirit"
        description="Learn why Reignite Spirit was created, what the platform provides, and how it helps professionals make more informed career transition decisions."
        path="/about"
        image="/og-about.jpg"
        type="website"
        author="Reignite Spirit"
        keywords="about Reignite Spirit, career intelligence platform, career transition planning, career resilience"
        schema={aboutPageSchema}
      />

      <article className="max-w-5xl mx-auto">
        <header className="text-center mb-14">
          <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.25em] text-sm">
            About Reignite Spirit
          </p>

          <h1 className="text-4xl md:text-6xl font-extrabold mt-4">
            Career decisions deserve more than guesswork
          </h1>

          <p className="mt-6 max-w-3xl mx-auto text-lg text-white/70 leading-relaxed">
            Reignite Spirit is a career intelligence and transition-planning
            platform designed to help professionals understand career risk,
            identify transferable strengths and explore practical pathways
            toward more resilient work.
          </p>
        </header>

        <div className="space-y-8">
          <section className="rounded-3xl border border-white/10 bg-white/5 p-7 md:p-10">
            <h2 className="text-3xl font-bold mb-4">
              Why the platform was created
            </h2>

            <p className="text-white/70 leading-relaxed">
              Career disruption is rarely caused by one factor alone.
              Automation, generative AI, changing hiring expectations,
              location-specific opportunities, financial pressure and personal
              responsibilities can all influence a career decision.
            </p>

            <p className="text-white/70 leading-relaxed mt-4">
              Reignite Spirit was created to bring these factors together in a
              more structured way. The platform helps users examine their
              current position, understand possible risks and consider
              transition options without relying only on generic job lists or
              broad career advice.
            </p>
          </section>

          <section className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                What Reignite Spirit provides
              </h2>

              <ul className="space-y-3 text-white/70">
                <li>Career exposure and resilience insights</li>
                <li>Transferable-skill identification</li>
                <li>Localized career pathway exploration</li>
                <li>Career transition planning support</li>
                <li>Financial runway estimation</li>
                <li>Career intelligence articles and resources</li>
              </ul>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Our approach
              </h2>

              <p className="text-white/70 leading-relaxed">
                The platform combines user-provided information with
                structured occupational data, transferable-skill analysis,
                automation-risk indicators and localized labour-market
                context.
              </p>

              <p className="text-white/70 leading-relaxed mt-4">
                Recommendations are intended to support informed decision
                making. They do not guarantee employment, salary, promotion or
                a particular career outcome.
              </p>
            </div>
          </section>

          <section className="rounded-3xl border border-[#EB6AB5]/30 bg-[#EB6AB5]/5 p-7 md:p-10">
            <h2 className="text-3xl font-bold mb-4">
              Our mission
            </h2>

            <p className="text-white/75 leading-relaxed">
              Our mission is to help professionals move from career anxiety to
              structured action by making career risk, transferable skills,
              transition options and financial readiness easier to understand.
            </p>
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-7 md:p-10">
            <h2 className="text-3xl font-bold mb-5">
              Our values
            </h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <h3 className="font-bold text-[#EB6AB5]">
                  Practical clarity
                </h3>
                <p className="text-white/65 mt-2">
                  Present complex career information in a form users can
                  understand and act on.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-[#EB6AB5]">
                  Responsible guidance
                </h3>
                <p className="text-white/65 mt-2">
                  Clearly communicate assumptions, limitations and areas where
                  professional advice may be required.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-[#EB6AB5]">
                  Human-centred transitions
                </h3>
                <p className="text-white/65 mt-2">
                  Consider experience, responsibilities, location, financial
                  readiness and personal constraints.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-[#EB6AB5]">
                  Continuous improvement
                </h3>
                <p className="text-white/65 mt-2">
                  Review methodology, content and data assumptions as labour
                  markets and technology evolve.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-2xl font-bold mb-4">
              Learn more
            </h2>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/methodology"
                className="px-5 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
              >
                View our methodology
              </Link>

              <Link
                to="/data-sources"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5]"
              >
                Review data sources
              </Link>

              <Link
                to="/contact"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5]"
              >
                Contact us
              </Link>
            </div>
          </section>
        </div>
      </article>
    </main>
  );
}